/**
 * @module AdapterCard
 * @package @geenius/adapters/react-css
 * @description Renders the vanilla CSS adapter summary card for the React CSS
 * variant. The card surfaces provider status, latency, and domain metadata for
 * one adapter domain in the dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import { DOMAIN_DESCRIPTIONS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "react";
import { cx } from "../lib/cx";
import { AdapterDomainIcon } from "./AdapterDomainIcon";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the React CSS dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property className Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  className?: string;
}

/**
 * Renders a clickable React CSS summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns A focusable article when selectable, otherwise a static article.
 */
export function AdapterCard({
  domain,
  status,
  onClick,
  className = "",
}: AdapterCardProps): JSX.Element {
  const body = (
    <>
      <div className="gn-adapters-card__icon">
        <AdapterDomainIcon domain={domain} />
      </div>
      <div className="gn-adapters-card__body">
        <div className="gn-adapters-card__header">
          <h3 className="gn-adapters-card__name">{DOMAIN_LABELS[domain]}</h3>
          <AdapterStatusBadge status={status.status} />
        </div>
        <p className="gn-adapters-card__desc">{DOMAIN_DESCRIPTIONS[domain]}</p>
        {status.provider && status.provider !== "none" && (
          <div className="gn-adapters-card__meta">
            <span className="gn-adapters-card__provider">
              {status.provider}
            </span>
            {status.latency !== undefined && (
              <span className="gn-adapters-card__latency">
                {status.latency}ms
              </span>
            )}
          </div>
        )}
        {status.error && (
          <p className="gn-adapters-card__error">{status.error}</p>
        )}
      </div>
    </>
  );

  if (!onClick) {
    return (
      <article
        className={cx(
          "gn-adapters-card",
          "gn-adapters-card--static",
          className,
        )}
        aria-label={`${DOMAIN_LABELS[domain]} adapter - ${status.status}`}
      >
        {body}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx("gn-adapters-card", className)}
      aria-label={`${DOMAIN_LABELS[domain]} adapter - ${status.status}`}
    >
      {body}
    </button>
  );
}
